import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { produccionApi, errorMessage } from '../utils/produccionApi';
import { formatKg, formatDate, formatDateTime, parseKgOrZero } from '../utils/produccion';
import '../styles/produccion.css';

// Robin's Cerrar: review what the team finished, correct the figures per raw
// lot (information only: they move no stock), add notes and close.
const FIELDS = [
  { key: 'returned', label: 'Devuelto' },
  { key: 'consumed', label: 'Usado' },
  { key: 'merma', label: 'Merma' },
];
const BAD_CELL = 'Revisa las cifras: van en kg, 0 o más.';
const NOT_READY = { finalizada: 'Esta orden ya está cerrada.' };

const round3 = (n) => Math.round(n * 1000) / 1000 + 0; // + 0 turns -0 into 0
const rowKey = (row) => `${row.sku_id}|${row.batch}`;
const kg = (x) => `${formatKg(Number(x))} kg`;
const expiry = (date) => (date ? `vence ${formatDate(date)}` : 'sin vencimiento');

function ProduccionCerrar() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { po_number: poNumber } = useParams();
  const orderPath = `orders/${encodeURIComponent(poNumber)}`;

  const [order, setOrder] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [cells, setCells] = useState({});
  const [notes, setNotes] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await produccionApi.get(`${orderPath}/`, token);
      if (cancelled) return;
      if (!res.ok) {
        setLoadError(errorMessage(res));
        return;
      }
      const balance = (res.data.transformation && res.data.transformation.balance) || [];
      // Start from the kardex: what Terminar wrote.
      setCells(Object.fromEntries(balance.filter((row) => row.raw).map((row) => [
        rowKey(row), Object.fromEntries(FIELDS.map(({ key }) => [key, String(Number(row[key])).replace('.', ',')])),
      ])));
      setOrder(res.data);
    })();
    return () => { cancelled = true; };
  }, [orderPath, token]);

  const header = (
    <div className="prod-retiro-head">
      <div>
        <h1 className="prod-title">Cerrar producción</h1>
        <div className="prod-sub" style={{ margin: 0 }}>
          {order && order.product ? `${poNumber} · ${order.product.name}` : poNumber}
        </div>
      </div>
      <button type="button" className="prod-btn ghost prod-btn-small" onClick={() => navigate(`/production/${poNumber}`)}>
        ← Volver
      </button>
    </div>
  );

  if (loadError) {
    return <div className="prod-page">{header}<div className="prod-bad prod-error">{loadError}</div></div>;
  }
  if (!order) {
    return <div className="prod-page">{header}<div className="prod-sub">Cargando…</div></div>;
  }
  if (order.stage !== 'terminada') {
    return (
      <div className="prod-page">
        {header}
        <div className="prod-card amber">
          <div className="n">{NOT_READY[order.stage] || 'Esta orden todavía no está terminada.'}</div>
        </div>
        <button type="button" className="prod-btn blue" onClick={() => navigate('/production')}>Volver a Producción</button>
      </div>
    );
  }

  const balance = order.transformation.balance || [];
  const rows = balance.filter((row) => row.raw).map((row) => {
    const values = Object.fromEntries(FIELDS.map(({ key }) => [key, parseKgOrZero(cells[rowKey(row)][key])]));
    const valid = FIELDS.every(({ key }) => values[key] !== null);
    const saldo = valid ? round3(Number(row.pulled) - values.returned - values.consumed - values.merma) : null;
    return { row, values, valid, saldo };
  });

  let reason = '';
  if (rows.some((r) => !r.valid)) reason = BAD_CELL;
  else {
    const off = rows.find((r) => Math.abs(r.saldo) >= 0.0005);
    if (off) reason = `${poNumber}: el lote ${off.row.batch} no cuadra por ${formatKg(Math.abs(off.saldo))} kg`;
  }

  const changes = reason ? [] : rows.flatMap(({ row, values }) => FIELDS
    .filter(({ key }) => values[key] !== Number(row[key]))
    .map(({ key, label }) => `${row.batch} · ${label} ${formatKg(Number(row[key]))} → ${formatKg(values[key])}`));

  const setCell = (row, key, text) => setCells((current) => ({
    ...current, [rowKey(row)]: { ...current[rowKey(row)], [key]: text },
  }));

  const close = async () => {
    if (busyRef.current || reason) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    const body = {
      lots: rows.map(({ row, values }) => ({ sku_id: row.sku_id, batch: row.batch, ...values })),
      notes: notes.trim(),
    };
    const res = await produccionApi.send('POST', `${orderPath}/close/`, body, token);
    busyRef.current = false;
    setBusy(false);
    if (res.ok) {
      navigate(`/production/${poNumber}?cerrada=1`);
      return;
    }
    setError(errorMessage(res));
  };

  const locked = confirming || busy;
  const counts = [['Bolsa', order.bolsa], ['Interfolio', order.interfolio], ['Etiqueta', order.etiqueta]]
    .filter(([, value]) => value !== null && value !== undefined);

  return (
    <div className="prod-page">
      {header}

      <div className="prod-card">
        <div className="d">{`Terminada por ${order.finished_by || '—'} · ${formatDateTime(order.finished_at)}`}</div>
        {counts.length > 0 && <div className="d">{counts.map(([label, value]) => `${label} ${value}`).join(' · ')}</div>}
      </div>
      {(order.outputs || []).map((o) => (
        <div className="prod-card green" key={`${o.sku.id}|${o.batch}`}>
          <div className="n">{o.sku.name}</div>
          <div className="prod-batch">{o.batch}</div>
          <div className="d">
            {`${expiry(o.expiration_date)} · ${o.rack}-${o.shelf} · ${formatKg(Number(o.quantity))} ${o.sku.stock_unit === 'un' ? 'un.' : 'kg'}`}
            {o.weight ? ` · ${kg(o.weight)}` : ''}
          </div>
        </div>
      ))}
      {(order.supplies || []).length > 0 && (
        <div className="prod-trace">
          {order.supplies.map((s) => (
            <div className="row" key={`${s.sku.id}|${s.batch}`}>
              {`${s.sku.name} · lote ${s.batch} · ${s.rack}-${s.shelf} · ${formatKg(Number(s.quantity))}`}
            </div>
          ))}
        </div>
      )}

      <div className="prod-lbl" style={{ marginTop: 18 }}>Cuadratura por lote</div>
      <div className="prod-note">Las cifras corregidas son información: no mueven stock ni kardex.</div>
      <div className="prod-table-wrap">
        <table className="prod-table">
          <thead>
            <tr>
              <th>Producto</th><th>Lote</th><th>Retirado</th><th>Devuelto</th><th>Usado</th><th>Merma</th><th>Saldo</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ row, values, saldo }) => (
              <tr key={rowKey(row)}>
                <td>{row.sku_code}</td>
                <td>{row.batch}</td>
                <td>{formatKg(Number(row.pulled))}</td>
                {FIELDS.map(({ key, label }) => (
                  <td key={key}>
                    <input
                      className={`prod-input kg${values[key] !== Number(row[key]) ? ' prod-edited' : ''}`}
                      inputMode="decimal"
                      aria-label={`${label} ${row.batch}`}
                      value={cells[rowKey(row)][key]}
                      disabled={locked}
                      onChange={(e) => setCell(row, key, e.target.value)}
                    />
                  </td>
                ))}
                <td className={saldo === 0 ? 'prod-ok' : 'prod-bad'}>
                  {saldo === null ? '—' : (saldo === 0 ? '0 ✓' : formatKg(saldo))}
                </td>
              </tr>
            ))}
            {balance.filter((row) => !row.raw).map((row) => (
              <tr key={rowKey(row)}>
                <td>{row.sku_code}</td>
                <td>{row.batch}</td>
                <td>{formatKg(Number(row.pulled))}</td>
                <td>{formatKg(Number(row.returned))}</td>
                <td>{formatKg(Number(row.consumed))}</td>
                <td>{formatKg(Number(row.merma))}</td>
                <td className="prod-ok">✓</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <label className="prod-lbl" style={{ display: 'block', marginTop: 14 }}>
        Notas de cierre
        <textarea
          className="prod-input"
          rows={2}
          aria-label="Notas de cierre"
          value={notes}
          disabled={locked}
          onChange={(e) => setNotes(e.target.value)}
        />
      </label>

      {confirming ? (
        <div className="prod-confirm">
          <div className="prod-panel-q">{`¿Cerrar ${poNumber}? Después no se puede editar.`}</div>
          {changes.length > 0
            ? <ul className="prod-changes">{changes.map((text) => <li key={text}>{text}</li>)}</ul>
            : <div className="prod-sub">Sin cambios en las cifras.</div>}
          {error && <div className="prod-bad prod-error">{error}</div>}
          <div className="prod-submit-row">
            <button type="button" className="prod-btn green" disabled={busy} onClick={close}>
              {busy ? 'Cerrando…' : 'Sí, cerrar'}
            </button>
            <button type="button" className="prod-btn ghost" disabled={busy} onClick={() => { setConfirming(false); setError(''); }}>
              Volver
            </button>
          </div>
        </div>
      ) : (
        <div className="prod-submit-row">
          <button type="button" className="prod-btn green" disabled={Boolean(reason)} onClick={() => setConfirming(true)}>
            Cerrar producción
          </button>
          {reason && <span className="prod-sub" style={{ margin: 0 }}>{reason}</span>}
        </div>
      )}
    </div>
  );
}

export default ProduccionCerrar;
